import type { PlatformName } from '@/lib/types'
import { FaFacebook, FaTwitter, FaMedium, FaGoogle, FaInstagram, FaLinkedin, FaEnvelope } from 'react-icons/fa'
import { SiSendgrid, SiGmail } from 'react-icons/si'

/**
 * Human-readable labels for each platform.
 */
const LABELS: Record<PlatformName, string> = {
  facebook: 'Facebook',
  twitter: 'Twitter/X',
  medium: 'Medium',
  google: 'Google',
  sendgrid: 'SendGrid',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  gmail: 'Gmail',
  outlook: 'Outlook',
}

/**
 * Official brand colors for each platform.
 */
const BRAND_COLORS: Record<PlatformName, string> = {
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  medium: '#000000',
  google: '#4285F4',
  sendgrid: '#0078D4',
  instagram: '#E4405F',
  linkedin: '#0A66C2',
  gmail: '#EA4335',
  outlook: '#0078D4',
}

/**
 * Return a human-readable label for a platform.
 * Falls back to the raw platform string for unknown values.
 */
export function getPlatformLabel(p: PlatformName): string {
  return LABELS[p] ?? (p as string)
}

type IconComponent = React.ComponentType<{ size?: number | string; color?: string }>

/**
 * Brand icon components for each platform.
 */
const ICONS: Record<PlatformName, IconComponent> = {
  facebook: FaFacebook,
  twitter: FaTwitter,
  medium: FaMedium,
  google: FaGoogle,
  sendgrid: SiSendgrid,
  instagram: FaInstagram,
  linkedin: FaLinkedin,
  gmail: SiGmail,
  outlook: FaEnvelope,
}

/**
 * Return a branded icon component for a platform with official brand color.
 */
export function getPlatformIcon(p: PlatformName): React.ComponentType<{ size?: number | string }> {
  const IconComponent = ICONS[p] ?? FaGoogle
  const color = BRAND_COLORS[p] ?? '#000000'

  return function ColoredIcon(props: { size?: number | string }) {
    return <IconComponent {...props} color={color} />
  }
}
